import { ActiveItemWrapper } from '@/components/features/Editor/components/ActiveItemWrapper';
import { type SectionProps } from '@/components/features/Editor/utils';
import { Image } from '@/components/shared/Image';
import { AspectRatio, Grid, GridItem } from '@chakra-ui/react';
import { MotionBox } from '@sphere/ui';
import { useInView } from 'framer-motion';
import dynamic from 'next/dynamic';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useWindowSize } from 'react-use';

const ReactPlayer = dynamic(() => import('react-player'), { ssr: false });

interface Props extends SectionProps {
  isPreview?: boolean;
  activeItem?: 'video' | 'image';
}

export const VideoAndImageBlock = ({ image, video, isPreview, activeItem }: Props) => {
  const { width } = useWindowSize();
  const [imageHeight, setImageHeight] = useState<number | undefined>(undefined);
  const videoRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(videoRef, { margin: '-10% 0% -10% 0%' });

  useEffect(() => {
    setImageHeight(
      isPreview
        ? (videoRef.current?.getBoundingClientRect().height || 0) * 2 + 12 // Multiply by 2 to compensate for the 2x scaling of the preview and add 12 to compensate for the active item border
        : videoRef.current?.getBoundingClientRect().height,
    );
  }, [width, isPreview]);

  return (
    <Grid
      py={{ base: 'space.40', md: 'space.96' }}
      gap={{ base: 'space.4', lg: 'space.8' }}
      templateColumns="repeat(3, 1fr)"
      overflow="hidden"
      bg="black"
      maxW="container.3xl"
      mx="auto"
    >
      <GridItem colSpan={{ base: 3, lg: 2 }}>
        <MotionBox
          initial={{ opacity: isPreview ? 1 : 0, translateX: isPreview ? 0 : '-200px' }}
          whileInView={{ opacity: 1, translateX: 0 }}
          viewport={{ amount: 0.5, once: true }}
          transition={{ duration: 0.3 }}
        >
          <ActiveItemWrapper isActive={activeItem === 'video'} isPreview={isPreview}>
            <AspectRatio
              ratio={16 / 9}
              ref={videoRef}
              pointerEvents={isPreview ? 'none' : undefined}
            >
              {video?.src ? (
                <ReactPlayer
                  playing={isPreview ? undefined : isInView}
                  height="100%"
                  width="100%"
                  url={video.src}
                  muted
                />
              ) : (
                <Fragment />
              )}
            </AspectRatio>
          </ActiveItemWrapper>
        </MotionBox>
      </GridItem>
      <GridItem colSpan={{ base: 3, lg: 1 }}>
        <MotionBox
          pos="relative"
          initial={{ opacity: isPreview ? 1 : 0, translateX: isPreview ? 0 : '200px' }}
          whileInView={{ opacity: 1, translateX: 0 }}
          viewport={{ amount: 0.5, once: true }}
          transition={{ duration: 0.3 }}
          h={imageHeight}
        >
          <ActiveItemWrapper isActive={activeItem === 'image'} h="100%" isPreview={isPreview}>
            {image?.src && (
              <Image
                src={image.src}
                alt=""
                objectFit="cover"
                fill
                pointerEvents={isPreview ? 'none' : undefined}
              />
            )}
          </ActiveItemWrapper>
        </MotionBox>
      </GridItem>
    </Grid>
  );
};
